import { transition, trigger, useAnimation } from '@angular/animations';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FormBuilder } from '@angular/forms';

import { ApiConfigManager } from '@sg/api/base-api-lib/src';
import { UsersApi, UsersApiClient } from '@sg/api/users-api';
import { EnvironmentService, SgEntityBasedSession, UserSessionService } from '@sg/common/src';
import { AsyncTracker } from '@sg/utils/src';
import { collapse, expand } from '@sg/ui-kit/animations';
import { CookiesSettingsModel } from '../cookies-settings/CookiesSetting.model';

@Component({
  selector: 'sg-cookies-consent-dialog',
  templateUrl: './cookies-consent-dialog.component.html',
  styleUrls: ['./cookies-consent-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      transition('void => *', useAnimation(expand, { params: { timing: 0.15, timingFn: 'ease-out' } })),
      transition('* => void', useAnimation(collapse, { params: { timing: 0.15, timingFn: 'ease-out' } })),
    ]),
  ],
  standalone: false,
})
export class CookiesConsentDialogComponent {
  logoSrc = this.env.branding.logo;
  appName = this.env.appName;
  /** Mixpanel is a lender-app-only vendor; its naming/consent only apply in the lender app. */
  isLenderApp = this.env.appNameWithoutPrefix === 'lender';

  tracker = new AsyncTracker();

  private _isCustomeSettingsDisplayed$ = new BehaviorSubject(false);
  isCustomeSettingsDisplayed$ = this._isCustomeSettingsDisplayed$.asObservable();

  customSettings = this.fb.control<CookiesSettingsModel>({ analytical: true, mixpanel: true });

  constructor(
    private env: EnvironmentService,
    private userSession: UserSessionService<any>,
    public session: SgEntityBasedSession,
    private cfg: ApiConfigManager,
    private usersApi: UsersApiClient,
    private fb: FormBuilder
  ) {}

  toggleCustomSettings() {
    this._isCustomeSettingsDisplayed$.next(!this._isCustomeSettingsDisplayed$.value);
  }

  agreeUserConsent(): void {
    const customized = this._isCustomeSettingsDisplayed$.value;
    const existing = this.session.user.userSettings.trackingConsent;
    // On a new-vendor re-prompt the main "Accept" must leave each already-answered analytics vendor
    // exactly as it was (default true only for a never-asked vendor); the customize panel applies a
    // single analytical choice to all three.
    const analytics = (stored: boolean | undefined): boolean =>
      customized ? this.customSettings.value.analytical : (stored ?? true);
    // Mixpanel is lender-app-only: set it only from the lender dialog; elsewhere keep the stored
    // value untouched, so the lender app still re-prompts a user who never saw the Mixpanel consent.
    const mixpanel = this.isLenderApp
      ? customized
        ? this.customSettings.value.mixpanel
        : (existing?.mixpanel ?? true)
      : existing?.mixpanel;
    const userSettings: UsersApi.UserSettingsDto = Object.assign(this.session.user.userSettings, {
      trackingConsent: {
        hotjar: analytics(existing?.hotjar),
        heap: analytics(existing?.heap),
        pendo: analytics(existing?.pendo),
        mixpanel,
      },
    });
    this.usersApi
      .postUsersUserSettings(userSettings, { observe: 'body' })
      .pipe(this.tracker.pipe)
      .subscribe(() => window.location.reload());
  }
}
